/**
 * CollagenPage — iter733 (Feb 2026)
 *
 * Dedicated public page that hosts the LandingCollagenType3Widget
 * (Collagen Type-3 gamified education + food scanner).
 * Route: /tools/collagen.
 */
import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LandingNav, COPY } from "@/pages/LandingPage";
import LandingCollagenType3Widget from "@/components/LandingCollagenType3Widget";

export default function CollagenPage() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const t = COPY[language] || COPY.bg;

  return (
    <div className={isDark ? "bg-slate-950 min-h-screen" : "bg-white min-h-screen"} data-testid="collagen-page">
      <Helmet>
        <title>{bg ? "Колаген тип III · Nutramilo" : "Collagen Type-3 · Nutramilo"}</title>
        <meta
          name="description"
          content={bg
            ? "Интерактивна игра за Колаген тип III: научи ключовите кофактори (Vit C, глицин, пролин, Zn, Cu) и сканирай храни в реално време."
            : "Interactive Collagen Type-3 game: learn key cofactors (Vit C, glycine, proline, Zn, Cu) and scan foods in real time."}
        />
        <link rel="canonical" href="https://nutramilo.com/tools/collagen" />
      </Helmet>

      <LandingNav t={t} />

      {/* Compact hero */}
      <section className={`relative overflow-hidden pt-10 pb-6 px-4 sm:px-6 lg:px-8 ${
        isDark ? "bg-gradient-to-b from-slate-950 via-purple-950/30 to-slate-950" : "bg-gradient-to-b from-white via-purple-50/40 to-white"
      }`}>
        <div className="max-w-4xl mx-auto text-center">
          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
            isDark ? "bg-purple-500/15 text-purple-300 border border-purple-500/30" : "bg-purple-100 text-purple-700 border border-purple-300"
          }`}>
            <Sparkles className="w-3 h-3" />
            {bg ? "Игра · Публична" : "Game · Public"}
          </div>
          <h1 className={`text-3xl sm:text-4xl lg:text-5xl font-black mt-3 tracking-tight ${
            isDark ? "text-white" : "text-slate-900"
          }`}>
            {bg ? "Колаген " : "Collagen "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-fuchsia-400">
              {bg ? "тип III" : "Type-3"}
            </span>
          </h1>
          <p className={`mt-3 text-sm sm:text-base max-w-2xl mx-auto ${isDark ? "text-slate-300" : "text-slate-600"}`}>
            {bg
              ? "Научи защо COL3 определя плътността на кожата, гърдите, съдовете и ставите. Играй, сканирай храни и виж кофакторното си покритие."
              : "Learn why COL3 governs skin, breast, vascular and joint density. Play, scan foods, and see your cofactor coverage."}
          </p>
        </div>
      </section>

      {/* The actual widget */}
      <LandingCollagenType3Widget />

      {/* Cross-link back to Scanner tool + register */}
      <section className={`py-10 px-4 sm:px-6 lg:px-8 ${isDark ? "bg-slate-950" : "bg-white"}`}>
        <div className="max-w-3xl mx-auto text-center">
          <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            {bg ? "Виж и другия ни безплатен инструмент:" : "See our other free tool:"}
          </p>
          <div className="mt-3 flex flex-col sm:flex-row justify-center gap-2">
            <Link to="/tools/scanner" data-testid="collagen-cross-scanner">
              <Button variant="outline" className={isDark ? "border-white/20 text-slate-200 hover:bg-white/10" : ""}>
                {bg ? "AI Nutrition Facts Scanner" : "AI Nutrition Facts Scanner"}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <Link to="/register" data-testid="collagen-cross-register">
              <Button className="gradient-primary text-white font-bold">
                {bg ? "Регистрация · пълен анализ" : "Register · full analysis"}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
